import React, { FC, useEffect, useState } from "react";
import LoginLayout from "../components/LoginLayout";
import { useParams, useNavigate } from 'react-router-dom';
import { reduxForm, InjectedFormProps, Field as ReduxField } from 'redux-form';
import { useIntl } from 'react-intl';

import { renderTextField } from "../components/LoginForm";
import Button from '@mui/material/Button';
import { CalloutStyle } from "./PasswordResetRequestPage"; 
import {
  containsCaps,
  containsNumbers,
  containsSymbols,
  matchesPwdLength,
  required
} from "components/validation/ValidationUtils";

interface UrlProps extends Record<string, string> {
  userId: string;
  token: string;
}

interface FormValues {
  newPwd: string;
  newPwdConf: string;
}

const Field = ReduxField as unknown as React.ComponentType<any>;

const PasswordResetPage: FC<InjectedFormProps<FormValues>> = ({ handleSubmit }) => {
  const params = useParams<UrlProps>();
  const userId = params.userId;
  const token = params.token;

  const intl = useIntl();
  const navigate = useNavigate();

  const [tokenValid, setTokenValidState] = useState(false);
  const [canRender, setCanRenderState] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [errorString, setErrorString] = useState('test');
  const [calloutStyleState, setCalloutStyleState] = useState<CalloutStyle>({
    opacity: 0,
    margin: '0px',
    padding: '0px'
  });

  const onKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit!(onSubmit)();
  };

  const onSubmit = async (values: FormValues) => {
    if (values.newPwd !== values.newPwdConf) {
      setErrorState(true);
      setErrorString('pwdsDontMatch');
      setCalloutStyleState({ opacity: 1, margin: '20px 0', padding: '20px' });
      setTimeout(() => {
        setCalloutStyleState({ opacity: 0, margin: '0', padding: '0' });
      }, 10000);
      return;
    }

    const res = await fetch('/api/resetPassword', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, password: values.newPwd })
    });

    if (res.status === 400) {
      setErrorState(true);
      setErrorString('password.NotNew');
    } else if (res.status >= 500) {
      setErrorState(true);
      setErrorString('server.error');
    } else if (res.status === 200) {
      setErrorState(false);
      setTimeout(() => navigate('/'), 10000);
    } else {
      setErrorString('test');
    }

    setCalloutStyleState({ opacity: 1, margin: '20px 0', padding: '20px' });
    setTimeout(() => {
      setCalloutStyleState({ opacity: 0, margin: '0', padding: '0' });
    }, 10000);
  };

  useEffect(() => {
    fetch('/api/checkTokenExpiry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: userId, token })
    }).then(r => {
      setTokenValidState(r.status === 200);
      setCanRenderState(true);
    });
  }, [userId, token]);

  if (!canRender) return null;
  if (!tokenValid) return <h1>{intl.formatMessage({ id: 'tokenExpired' })}</h1>;

  return (
    <LoginLayout>
      <div className="body-inner">
        <div className="card bg-color-light">
          <div className="card-content theme-light">
            <section className="logo text-center">
              <h3 style={{ color: '#0054ff' }}>{intl.formatMessage({ id: 'passwordReset' })}</h3>
            </section>

            <form className="form-horizontal" onSubmit={handleSubmit!(onSubmit)}>
              <fieldset>
                <div className="form-group">
                  <Field
                    component={renderTextField}
                    label={intl.formatMessage({ id: 'user.newPwd' })}
                    name="newPwd"
                    margin="normal"
                    validate={[required, containsSymbols, containsNumbers, containsCaps, matchesPwdLength]}
                    fullWidth
                    onKeyUp={onKeyUp}
                    type="password"
                    InputLabelProps={{ shrink: true }}
                  />
                  <Field
                    component={renderTextField}
                    label={intl.formatMessage({ id: 'user.newPwdConf' })}
                    name="newPwdConf"
                    margin="normal"
                    validate={[required]}
                    fullWidth
                    type="password"
                    onKeyUp={onKeyUp}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
              </fieldset>
              <div className="card-action no-border text-right">
                <Button type="submit" color="primary">{intl.formatMessage({ id: 'submit' })}</Button>
              </div>
            </form>

            <div style={{
              opacity: calloutStyleState.opacity,
              margin: calloutStyleState.margin,
              padding: calloutStyleState.padding,
              transition: `
                opacity 0.5s ease-in-out,
                max-height 0.5s ease-in-out,
                padding 0.5s ease-in-out,
                margin 0.5s ease-in-out`
            }} className={`callout callout-${errorState ? 'danger' : 'success'} no-margin-bottom`}>
              <p>{errorState ? intl.formatMessage({ id: errorString }) : intl.formatMessage({ id: 'passwordChanged' })}</p>
            </div>
          </div>
        </div>
      </div>
    </LoginLayout>
  );
};

export default reduxForm<FormValues>({
  form: 'passwordReset',
})(PasswordResetPage);